// Steam vent art, drawn in code from the palette (docs/STYLE.md). Server rule: an open vent cuts enemy sight through and into a
// circle of CONFIG.vent.radius (server/src/ai.ts ventBlocks). So the picture is the rule: a ground-layer footprint ring that is
// exactly that radius, plus a few sparse wisps. Steam is drawn on the ground layer, under characters, pickups and markers, so it
// never covers a player, the "you" chevron or an attack warning, and it reads with lighting off. Everything is bounded:
// WISPS per open vent, vents capped by config.vent.maxActive.
import type { Graphics } from 'pixi.js';
import { CONFIG } from '../../common/src/data';
import { COLOR, PALETTE } from './theme';

const hexNum = (c: string) => parseInt(c.slice(1), 16);
/** Wisps drawn per open vent (half that with "Low effects"). */
export const WISPS = 7;
const FADE_SEC = 1.2;

const h01 = (a: number, b: number) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };

export interface Wisp { x: number; y: number; r: number; a: number }
/**
 * The wisps of one open vent at animation time `t`: small puffs that rise from the grate and drift out, always inside the footprint.
 * `left` is the seconds the vent stays open (from the snapshot). Pure, so a test can check the bounds.
 */
export function ventWisps(id: number, x: number, y: number, left: number, t: number, count = WISPS): Wisp[] {
  const R = CONFIG.vent.radius, fade = Math.min(1, Math.max(0, left) / FADE_SEC), out: Wisp[] = [];
  for (let i = 0; i < count; i++) {
    const life = 1.6 + h01(id, i) * 1.4, ph = (t / life + h01(id, i + 40)) % 1; // 0..1 along this wisp's rise
    const ang = h01(id, i + 80) * Math.PI * 2, rad = (0.15 + 0.6 * h01(id, i + 120)) * R * Math.min(1, ph * 1.6);
    out.push({ x: x + Math.cos(ang) * rad, y: y + Math.sin(ang) * rad * 0.8 - ph * 10, r: 2.5 + 3.5 * ph + h01(id, i + 160) * 1.5, a: 0.5 * fade * Math.sin(Math.PI * ph) });
  }
  return out;
}

/** The grate and, while open, the footprint of one vent. (x, y) is its centre in world px; `left` is seconds open (0 = closed). */
export function drawVent(g: Graphics, id: number, x: number, y: number, left: number, t: number, low: boolean) {
  // grate: a dark slotted plate on the pavement, always visible so a closed vent can be found and targeted
  g.roundRect(x - 7, y - 5, 14, 10, 2).fill({ color: hexNum(COLOR.ground), alpha: 0.85 }).stroke({ color: hexNum(PALETTE.slate), width: 1 });
  for (let k = -4; k <= 4; k += 4) g.rect(x + k - 0.75, y - 3, 1.5, 6).fill({ color: hexNum(PALETTE.slate) });
  if (left <= 0) return;
  const R = CONFIG.vent.radius, fade = Math.min(1, left / FADE_SEC), pulse = low ? 0 : 0.03 * Math.sin(t * 3 + id);
  g.circle(x, y, R).fill({ color: hexNum(PALETTE.offWhite), alpha: (0.16 + pulse) * fade });
  // the edge is a dashed ring (a shape signal, not color alone): the exact radius the server uses
  const dash = 12;
  for (let k = 0; k < dash; k++) { const a0 = (k / dash) * Math.PI * 2, a1 = a0 + (Math.PI * 2) / dash * 0.55; g.moveTo(x + Math.cos(a0) * R, y + Math.sin(a0) * R).arc(x, y, R, a0, a1).stroke({ color: hexNum(PALETTE.offWhite), width: 1.5, alpha: 0.6 * fade }); }
  g.circle(x, y, 2.5).fill({ color: hexNum(PALETTE.cyan), alpha: 0.9 * fade }); // the open valve
  for (const w of ventWisps(id, x, y, left, t, low ? Math.ceil(WISPS / 2) : WISPS)) g.circle(w.x, w.y, w.r).fill({ color: hexNum(PALETTE.offWhite), alpha: w.a });
}
